import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode, useEffect, useState } from 'react';
import type { AppFlags } from './app-flags';

// If LaunchDarkly never answers, fall through to "loaded" rather than leaving
// callers waiting forever on a flag set that will never arrive.
const FLAG_BOOTSTRAP_TIMEOUT_MS = 3000;

type LDFlagSet = {
  [key: string]: unknown;
};

interface FlagProps<
  FlagSet,
  FlagKey extends keyof FlagSet,
  Match = FlagSet[FlagKey],
> {
  flag: FlagKey;
  defaultValue?: Match;
  when?: Match;
  children?: ReactNode;
  fallback?: ReactNode | null;
}

const usePurposemedFlags = () => useFlags<AppFlags>();

/**
 * LDProvider renders children before flags arrive, so an empty flag set means
 * "still loading", not "every flag is off". Callers that would otherwise treat
 * an unresolved flag as off — redirecting, hiding a card, reporting an
 * analytics snapshot — must wait for this before reading `useFlagMatch`.
 */
const useFlagsLoaded = (): boolean => {
  const flags = useFlags();
  const [timedOut, setTimedOut] = useState(false);
  const hasFlags = Object.keys(flags).length > 0;

  useEffect(() => {
    if (hasFlags) return;

    const timer = setTimeout(
      () => setTimedOut(true),
      FLAG_BOOTSTRAP_TIMEOUT_MS,
    );

    return () => clearTimeout(timer);
  }, [hasFlags]);

  return hasFlags || timedOut;
};

const useFlagMatch = <
  FlagSet extends LDFlagSet = AppFlags,
  Key extends keyof FlagSet = keyof FlagSet,
>({
  flag,
  defaultValue,
  when,
}: Pick<FlagProps<FlagSet, Key>, 'flag' | 'defaultValue' | 'when'>) => {
  const flags = useFlags<FlagSet>();
  const result = flags[flag] || defaultValue;

  if (typeof result === 'boolean') {
    if (when !== undefined) {
      return result === when;
    } else {
      return result;
    }
  }

  if (when !== undefined) {
    return result === when;
  }

  return false;
};

const Flag = <
  FlagSet extends LDFlagSet = AppFlags,
  Key extends keyof FlagSet = keyof FlagSet,
>({
  children,
  flag,
  when,
  defaultValue,
  fallback = null,
}: FlagProps<FlagSet, Key>) => {
  const isMatch = useFlagMatch({ flag, defaultValue, when });

  return <>{isMatch ? children : fallback}</>;
};

export { Flag, useFlagMatch, useFlagsLoaded, usePurposemedFlags };
